import React, { useEffect, useState } from "react";
import VideoViewer from "@base/lib/VideoViewer";
import { getVariantImagesList, loadImage, loadImages, mainPicFirstHandle } from 'base/utils/getPicUrl';
import type { IImage } from 'base/interfaces/product';
import type { IVariant } from 'base/interfaces/product';
import ImagesListSwiper from "../ImagesListSwiper";
import ImageZoomViewer from "@app/common/ImagesLightbox/ImageZoomViewer";
import styles from "./index.module.less"
import { transImageSrc } from 'base/utils/trans-path';
import PreloadResources from '@base/lib/PreloadResources';
import delay from 'base/utils/delay'

interface IProps {
    shownVariant: IVariant;
    thumbImageSize?: number;
    bigPicRef?: React.RefObject<HTMLDivElement>;
}

type IInitialData = {
    imagesList: IImage[];
    selectedItem?: IImage;
};

const IMAGE_BIG_WIDTH = 700;

const getInitialData = (shownVariant?: IVariant): IInitialData => {
    if (!shownVariant) {
        return { imagesList: [] };
    }
    const { pics } = shownVariant
    let _images = getVariantImagesList(pics, shownVariant?.productVariantId)
    _images = mainPicFirstHandle(_images);

    return {
        imagesList: [..._images],
        selectedItem: _images?.[0]
    }
};

const ProductImagesLightbox: React.FC<IProps> = (props) => {
    const {
        shownVariant,
        thumbImageSize,
        bigPicRef
    } = props;

    const initialData = getInitialData(shownVariant);
    const [selectedItem, setSelectedItem] = useState<IImage | undefined>(initialData.selectedItem);
    const [imagesList, setImagesList] = useState<IImage[]>(initialData.imagesList);


    // 加载图片列表
    const handlePreLoadImages = () => {
        loadImages(imagesList
            .filter(img => img.type == 0) // 排除不是图片的
            .map((img) => transImageSrc(img?.picLink, thumbImageSize, ))
        )
    }
    const preloadImageSrc = imagesList.length ? transImageSrc(imagesList[0].picLink, thumbImageSize) : '';

    // 切换展示图
    const onChangeShowImage = async (e: IImage) => {
        loadImage(transImageSrc(e?.picLink, IMAGE_BIG_WIDTH, ))

        if (e.type === 1) {
            // 两个视频切换无用 先置空
            setSelectedItem(void 0)
            await delay(1)
        }
        setSelectedItem(e)
    }

    useEffect(() => {
        const initialData = getInitialData(shownVariant);
        setImagesList(initialData.imagesList);
        setSelectedItem(initialData.selectedItem);
    }, [shownVariant])

    useEffect(() => {
        if (!imagesList?.length) {
            return
        }
        if (document?.readyState == 'complete') {
            handlePreLoadImages()
        } else {
            if (typeof window !== 'undefined') {
                window.onload = function () {
                    handlePreLoadImages()
                }
            }
        }
    }, [imagesList])

    return (
        <>
            {/*预加载主图*/}
            <PreloadResources list={[preloadImageSrc]}/>
            <div className={`${styles.lightboxWrap} flex`}>
                <div className={`${styles.imagesBig} relative`}>
                    <div className={`${styles.imagesWrap} absolute`}>
                        {/*type类型 0：图片， 1：视频 */}
                        {selectedItem?.type === 0
                            ? (
                                <ImageZoomViewer
                                    imgShowWidth={thumbImageSize}
                                    imgBigWidth={IMAGE_BIG_WIDTH}
                                    imgUrl={selectedItem.picLink}
                                    alt={selectedItem.altText || shownVariant.name}
                                    bigPicRef={bigPicRef}
                                />
                            )
                            : selectedItem?.type === 1
                                ? (<VideoViewer src={selectedItem.picLink} visible/>)
                                : null
                        }
                    </div>
                </div>

                <ImagesListSwiper
                    imagesList={imagesList}
                    onClick={(e: IImage) => {
                        onChangeShowImage(e)
                    }}
                    alt={shownVariant.name}
                />
            </div>
        </>
    )
}

export default React.memo(ProductImagesLightbox);
